import React, { useEffect, useState } from "react";
import { Gem, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StarRating } from "@/components/common/StarRating";

const MAX = 1000;

function ReplyDialog({ review, onOpenChange, onSave, onRemove, busy }) {
  const [body, setBody] = useState("");
  useEffect(() => { if (review) setBody(review.reply?.body || ""); }, [review]);
  const trimmed = body.trim();
  const valid = trimmed.length >= 2 && trimmed.length <= MAX;

  return (
    <Dialog open={!!review} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-border bg-ivory p-0 sm:rounded-none" data-testid="review-reply-dialog">
        <form onSubmit={(e) => { e.preventDefault(); if (valid) onSave(trimmed); }} className="p-6 sm:p-8">
          <p className="inline-flex items-center gap-1.5 text-luxe text-[10px] text-gold"><Gem size={12} /> Replying as ÉLORA JEWELS</p>
          <DialogTitle className="mt-1 font-serif text-3xl font-light text-ink">{review?.reply ? "Edit your reply" : "Reply to this review"}</DialogTitle>
          <DialogDescription className="mt-1 text-sm text-muted-foreground">Your response is shown publicly beneath the customer's review.</DialogDescription>

          {review && (
            <blockquote className="mt-5 border-l-2 border-border pl-4 text-sm text-muted-foreground" data-testid="review-reply-quote">
              <StarRating rating={review.rating} size={12} />
              <p className="mt-1.5 line-clamp-3 italic">“{review.body}”</p>
              <p className="mt-1 text-[11px] uppercase tracking-widest">— {review.name}</p>
            </blockquote>
          )}

          <label htmlFor="reply-body" className="mt-5 block text-luxe text-[10px] text-ink">Your reply</label>
          <textarea
            id="reply-body"
            rows={5}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={MAX}
            placeholder="Thank you for choosing ÉLORA — we're delighted your piece arrived beautifully…"
            data-testid="review-reply-textarea"
            className="mt-1.5 w-full resize-none border border-border bg-white px-3.5 py-3 text-sm text-ink outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-gold"
          />
          <p className="mt-1 text-right text-[11px] text-muted-foreground">{body.length}/{MAX}</p>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            {review?.reply ? (
              <button type="button" onClick={onRemove} disabled={busy} data-testid="review-reply-remove" className="text-[11px] uppercase tracking-widest text-muted-foreground underline-offset-4 hover:text-red-600 hover:underline disabled:opacity-50">
                Remove reply
              </button>
            ) : <span />}
            <button type="submit" disabled={!valid || busy} data-testid="review-reply-submit" className="inline-flex items-center gap-2 bg-ink px-7 py-3.5 text-[11px] font-medium uppercase tracking-[0.18em] text-ivory transition-colors hover:bg-gold disabled:cursor-not-allowed disabled:bg-muted-foreground/60">
              {busy && <Loader2 size={14} className="animate-spin" />} {review?.reply ? "Update reply" : "Publish reply"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteReviewDialog({ review, onCancel, onConfirm, isDeleting }) {
  return (
    <AlertDialog open={!!review} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent className="rounded-none" data-testid="review-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this review?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently remove the review{review?.name ? ` by ${review.name}` : ""} and any attached photos. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none" data-testid="review-delete-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} disabled={isDeleting} data-testid="review-delete-confirm" className="rounded-none bg-red-600 text-white hover:bg-red-700">
            {isDeleting ? "Deleting…" : "Delete Review"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function ReviewAdminDialogs({ admin }) {
  return (
    <>
      <DeleteReviewDialog review={admin.pendingDelete} onCancel={() => admin.setPendingDelete(null)} onConfirm={admin.confirmDelete} isDeleting={admin.isDeleting} />
      <ReplyDialog review={admin.replyTarget} onOpenChange={(o) => !o && admin.setReplyTarget(null)} onSave={admin.saveReply} onRemove={admin.removeReply} busy={admin.isSavingReply} />
    </>
  );
}
